import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useGoBack } from "@/lib/useGoBack";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { ArrowLeft, Save, FileSpreadsheet, Loader2, ClipboardList, Users, FileOutput, Map, FileDown } from "lucide-react";

// Import ASE sub-components
import { useASEFormData } from "@/components/ase/useASEFormData";
import { ExportDestinationDialog } from "@/components/ExportDestinationDialog";
import { CLOUD_EXPORT_LABELS, uploadExportToCloud, saveBlobToDevice, formExportFilename } from "@/lib/cloudExports";
import { ASEMapReference } from "@/components/ase/ASEMapReference";
import ASEChecklistTab from "@/components/ase/ASEChecklistTab";
import ASECoordinatorTab from "@/components/ase/ASECoordinatorTab";
import ASESVCLOutputTab from "@/components/ase/ASESVCLOutputTab";
import ASESOWNotesTab, { buildSowNotesText } from "@/components/ase/ASESOWNotesTab";
import { HomeButton } from "@/components/HomeButton";

const ASEForm = () => {
  const { jobId } = useParams();
  const goBack = useGoBack(`/jobs/${jobId}`);
  const [exporting, setExporting] = useState(false);
  const [exportingWord, setExportingWord] = useState(false);
  const { job, poles, underground, mapConnections, mapRoutes, loading, saving, formData, handleChange, regeneratePathOfApproach, handleSave } = useASEFormData(jobId);

  const handleExport = async () => {
    setExporting(true);
    try {
      // Save current form data first and wait for confirmation
      const saveResponse = await axios.put(`/jobs/${jobId}/ase-data`, { ase_data: formData });
      if (saveResponse.status !== 200) {
        throw new Error("Failed to save data before export");
      }
      
      // Small delay to ensure data is persisted
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const response = await axios.get(`/jobs/${jobId}/export/ase`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', formExportFilename(job, "ASE Checklist", "xlsx"));
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("ASE Excel exported");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Export failed - " + (error.message || "Please try again"));
    } finally {
      setExporting(false);
    }
  };

  const handleWordExport = async () => {
    setExportingWord(true);
    try {
      const text = formData.sow_notes_override || buildSowNotesText(formData);
      const response = await axios.post(`/jobs/${jobId}/export/sow-docx`, { text }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', formExportFilename(job, "SOW Notes", "docx"));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("SOW Notes Word file downloaded");
    } catch (error) {
      toast.error("Word download failed");
    } finally {
      setExportingWord(false);
    }
  };

  const [pendingExport, setPendingExport] = useState(null);
  const [exportBusy, setExportBusy] = useState(false);

  const getAseExportBlob = async (type) => {
    await axios.put(`/jobs/${jobId}/ase-data`, { ase_data: formData });
    const jobName = job?.name || job?.job_number || jobId;
    if (type === "ase_excel") {
      const r = await axios.get(`/jobs/${jobId}/export/ase`, { responseType: "blob" });
      return { blob: new Blob([r.data]), filename: formExportFilename(job, "ASE Checklist", "xlsx") };
    }
    const text = formData.sow_notes_override || buildSowNotesText(formData);
    const r = await axios.post(`/jobs/${jobId}/export/sow-docx`, { text }, { responseType: "blob" });
    return { blob: new Blob([r.data]), filename: formExportFilename(job, "SOW Notes", "docx") };
  };

  const runPendingExport = async (dest) => {
    const type = pendingExport;
    setExportBusy(true);
    try {
      const res = await getAseExportBlob(type);
      if (dest === "device") {
        saveBlobToDevice(res.blob, res.filename);
        toast.success("Downloaded to device");
      } else {
        await uploadExportToCloud(jobId, type, res.filename, res.blob);
        toast.success("Saved to app cloud — see Cloud Exports on the job page");
      }
      setPendingExport(null);
    } catch (e) {
      toast.error(dest === "cloud" ? "Cloud save failed" : "Export failed");
    } finally {
      setExportBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button type="button" onClick={goBack} className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="ase-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <HomeButton />
            <div>
              <h1 className="font-heading text-lg sm:text-xl font-bold" data-testid="ase-houston-title">ASE FORM HOUSTON</h1>
              <p className="text-xs text-zinc-500 font-mono" data-testid="ase-header-job-type">{job?.job_type || "OCALC"}{job?.site_id ? ` • ${job.site_id}` : ""}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to={`/jobs/${jobId}/map`}>
              <Button variant="outline" size="sm" className="bg-green-50 border-green-300 hover:bg-green-100 text-green-700" data-testid="ase-map-btn">
                <Map className="w-4 h-4" />
                <span className="hidden sm:inline ml-1">Map</span>
              </Button>
            </Link>
            <Button onClick={handleSave} disabled={saving} variant="outline" size="sm" data-testid="save-ase-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span className="hidden sm:inline ml-1">Save</span>
            </Button>
            <Button onClick={() => setPendingExport("sow_docx")} disabled={exportBusy || exportingWord} variant="outline" size="sm" className="text-blue-700 border-blue-300 hover:bg-blue-50" data-testid="export-sow-word-btn">
              {exportBusy || exportingWord ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              <span className="hidden sm:inline ml-1">Word</span>
            </Button>
            <Button onClick={() => setPendingExport("ase_excel")} disabled={exporting || exportBusy} size="sm" data-testid="export-ase-btn">
              {exporting || exportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              <span className="hidden sm:inline ml-1">Export</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-6 space-y-6" data-testid="ase-form">
        {/* Legend */}
        <div className="flex items-center gap-4 text-xs font-mono bg-white p-3 rounded-lg border border-zinc-200">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-green-500"></span>
            Filled
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-amber-400"></span>
            Not Filled
          </span>
        </div>
        
        {/* On-screen reference: job map + span list (never exported) */}
        <ASEMapReference job={job} poles={poles} underground={underground} connections={mapConnections} routes={mapRoutes} />

        {/* 3-Tab Structure - EXACT match to Excel */}
        <Tabs defaultValue="ase-cklist" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6 bg-zinc-100 p-1 rounded-lg h-auto">
            <TabsTrigger 
              value="ase-cklist" 
              className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-blue-700 py-2"
              data-testid="tab-ase-cklist"
            >
              <ClipboardList className="w-4 h-4" />
              <span className="text-xs sm:text-sm">ASE CKLIST</span>
            </TabsTrigger>
            <TabsTrigger 
              value="coordinator-input" 
              className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-green-700 py-2"
              data-testid="tab-coordinator-input"
            >
              <Users className="w-4 h-4" />
              <span className="text-xs sm:text-sm">Coordinator Input</span>
            </TabsTrigger>
            <TabsTrigger 
              value="svcl-output" 
              className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-purple-700 py-2"
              data-testid="tab-svcl-output"
            >
              <FileOutput className="w-4 h-4" />
              <span className="text-xs sm:text-sm">SVCL Output</span>
            </TabsTrigger>
            <TabsTrigger 
              value="sow-notes" 
              className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-amber-700 py-2"
              data-testid="tab-sow-notes"
            >
              <ClipboardList className="w-4 h-4" />
              <span className="text-xs sm:text-sm">SOW Notes</span>
            </TabsTrigger>
          </TabsList>

          {/* Tab 1: ASE CKLIST */}
          <TabsContent value="ase-cklist">
            <ASEChecklistTab formData={formData} handleChange={handleChange} poles={poles} underground={underground} onRegeneratePath={regeneratePathOfApproach} />
          </TabsContent>

          {/* Tab 2: Coordinator Input */}
          <TabsContent value="coordinator-input">
            <ASECoordinatorTab formData={formData} handleChange={handleChange} />
          </TabsContent>

          {/* Tab 3: SVCL Output */}
          <TabsContent value="svcl-output">
            <ASESVCLOutputTab formData={formData} handleChange={handleChange} />
          </TabsContent>

          {/* Tab 4: SOW Notes (auto-generated, user-editable) */}
          <TabsContent value="sow-notes">
            <ASESOWNotesTab formData={formData} handleChange={handleChange} />
          </TabsContent>
        </Tabs>

        {/* Save/Export buttons */}
        <div className="flex justify-end gap-3 pt-4">
          <Button onClick={handleSave} disabled={saving} variant="outline" className="h-11 px-6">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
          <Button onClick={() => setPendingExport("ase_excel")} disabled={exporting || exportBusy} className="h-11 px-6 bg-blue-600 hover:bg-blue-700 text-white">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            ASE Export
          </Button>
        </div>
      </main>
      <ExportDestinationDialog
        open={!!pendingExport}
        onOpenChange={(v) => { if (!v) setPendingExport(null); }}
        label={CLOUD_EXPORT_LABELS[pendingExport] || ""}
        busy={exportBusy}
        onDevice={() => runPendingExport("device")}
        onCloud={() => runPendingExport("cloud")}
      />
    </div>
  );
};

export default ASEForm;
